import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "../../../components/CRUDcomponents/Actionbar/Modals/Search/Search";
import { Print } from "../../../components/CRUDcomponents/Actionbar/Modals/Print/Print";
import DynamicTable from "../../../components/Table/IndentTable";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { useSelector } from "react-redux";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiEndpoint = `${API_BASE_URL}/popage/indent`;

const formatCreationDate = (dateStr) => {
  if (!dateStr) return "N/A";
  // If already in dd/MM/yy or dd-mm-yyyy, just return as is
  if (/^\d{2}[\/-]\d{2}[\/-]\d{2,4}$/.test(dateStr)) return dateStr;
  // Try to parse as ISO or yyyy-mm-dd
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const PHIndent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });

  const navigate = useNavigate();
  const getEditFields = () => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options:
        dropdownData.projects.map((project) => ({
          value: project.id,
          label: project.name,
        })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options:
        dropdownData.types.map((type) => ({
          value: type.id,
          label: type.name,
        })) || [],
    },
  ];

  const fetchIndents = async () => {
    try {
      const apiEndpoint = `${API_BASE_URL}/adminpage/phindent`; // Using admin endpoint
      const response = await axios.get(apiEndpoint);
      const formattedIndents = response.data
        .map((indent) => ({
          ...indent,
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending",
          PO: indent.PO ? "Approved" : "Pending",
          PH: indent.PH ? "Approved" : "Pending",
          CreationDate: formatCreationDate(indent.CreationDate),
        }))
        .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));
      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
    } catch (error) {
      console.error("Error fetching indents:", error);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };

      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setFilteredIndents(indents);
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
      const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.Status,
        indent.ProjectName,
        indent.TypeName,
        indent.CreationDate?.toString(),
        indent.TaskSchedule,
        indent.L1,
        indent.L2,
        indent.PO,
        indent.PH
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
        );
      });
      setFilteredIndents(filtered);
  };

  const handleDateFilter = (filterOptions) => {
    const { dateFrom, dateTo, indentType } = filterOptions;
    let filtered = indents;

    // Apply date filter if dates are provided
    if (dateFrom && dateTo) {
      filtered = filtered.filter((indent) => {
        const creationDate = new Date(indent.CreationDate);
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
    }

    // Apply indent type filter if selected
    if (indentType) {
      filtered = filtered.filter((indent) => 
        indent.TypeName?.toLowerCase() === indentType.toLowerCase()
      );
    }

      setFilteredIndents(
        filtered.sort(
          (a, b) => new Date(b.CreationDate) - new Date(a.CreationDate)
        )
      );
  };

  const handleRedirect = (page) => {
    navigate(page);
  };

  const handleRowClick = (row) => {
    navigate(`/PHtable`, { state: { row } });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">PH Table</h3>
        </div>

        {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields()}
            apiEndpoint={apiEndpoint}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={fetchIndents}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole="project head"
            addLink="/addl2Indent"
            editRoute="/PHTable"
            searchQuery={searchQuery}
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={[
              { key: "IndentID", label: "Indent ID" },
              { key: "CreationDate", label: "Creation Date" },
              { key: "ProjectName", label: "Project" },
              { key: "TypeName", label: "Indent Type" },
              { key: "TaskSchedule", label: "Task Scheduled" },
              { key: "Status", label: "Status" },
              { key: "ItemCount", label: "Item Count" },
            ]}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={(row) => setSelectedRow(row)}
            onDoubleClick={(row) => handleRowClick(row)}
            rowKey="IndentID"
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole="admin"
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PHIndent;
